/**
 * Created by Tengfei Li on 2026-01-20
 * 已下发配置（已私有化）Tab 组件
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Space, Table, Dropdown, message, Modal } from 'antd';
import { SelectItem } from '../../components/select-item';
import { InputItem } from '../../components/inputItem';
import i18n from 'i18next';
import { TablePagination, MoreOutlined } from '../../components/lib';
import { useTableSelection } from './use-table-selection';
import { http } from '../../common/request';
import { HttpURLS } from '../../common/http-url-define';
import { resultModal } from '../../components/result-modal';
import tableMenu from '../../components/menu';
import EditDevices from './edit-devices';
import DeviceConfigTipModal from './device-config-tip-modal';
import styled from '@emotion/styled';
import { connect } from 'react-redux';
import actions from '../../redux/action';
import _ from 'lodash';

const SEND_STATUS_OPTIONS = [
  { label: i18n.t('sent'), value: 'true' },
  { label: i18n.t('unsent'), value: 'false' },
];

function SentDevicesTab(props) {
  const {
    selectedMacs,
    setSelectedMacs,
    rowSelection,
    addSelectedCode,
    clearSelected,
  } = useTableSelection();

  const { language, onRefreshConfigList, onRefreshProducts, initialConfigId } = props;

  const [mac, setMac] = useState('');
  const [productCode, setProductCode] = useState(null);
  const [privateConfigId, setPrivateConfigId] = useState(null);
  const [hadSend, setHadSend] = useState(null);
  const [editDeviceDialogVisible, setEditDeviceDialogVisible] = useState(false);
  const [editConfigTipVisible, setEditConfigTipVisible] = useState(false);
  const [editedConfigName, setEditedConfigName] = useState('');

  // 初始化时获取配置列表和产品列表
  useEffect(() => {
    onRefreshConfigList();
    onRefreshProducts(language);
    // 如果有传入初始配置ID，则设置筛选条件
    if (initialConfigId) {
      setPrivateConfigId(initialConfigId);
    }
  }, [language, onRefreshConfigList, onRefreshProducts, initialConfigId]);

  // 构建搜索 filter
  const buildFilter = useCallback((params = {}) => {
    return _.omitBy({
      hadPrivate: true,
      offset: 0,
      limit: 50,
      keyword: mac || undefined,
      productCode: productCode || undefined,
      privateConfigId: privateConfigId || undefined,
      hadSend: hadSend === 'true' ? true : hadSend === 'false' ? false : undefined,
      ...params,
    }, _.isNil);
  }, [mac, productCode, privateConfigId, hadSend]);

  // 防抖搜索
  const debouncedSearch = useCallback(
    _.debounce(() => {
      props.onRefreshDeviceList(language, buildFilter());
      clearSelected();
    }, 500),
    [buildFilter, language]
  );

  useEffect(() => {
    debouncedSearch();
    return () => {
      debouncedSearch.cancel();
    };
  }, [mac, productCode, privateConfigId, hadSend, debouncedSearch]);

  const deleteDevice = macList => {
    const replaceStr =
      macList.length === 1 ? macList?.[0] : ` ${macList.join(',')} `;
    Modal.confirm({
      icon: null,
      title: i18n.t('confirm_to_delete_device_title').replace('%$', replaceStr),
      content: i18n.t('confirm_to_delete_device_content'),
      okText: i18n.t('menu_delete'),
      cancelText: i18n.t('cancel'),
      onOk: () => {
        http('delete', HttpURLS.DEVICE_URL, { macList }).then(res => {
          if (res?.data?.code === 200) {
            props.onRefreshDeviceList(language, buildFilter());
            clearSelected();
            message.success(i18n.t('delete_success'));
          } else {
            message.error(i18n.t('delete_failed'));
          }
        }).catch(err => {
          message.error(i18n.t('delete_failed') + JSON.stringify(err));
        });
      },
    });
  };

  const tableColumns = [
    { title: 'MAC', dataIndex: 'mac', key: 'mac' },
    { title: 'SN', dataIndex: 'sn', key: 'sn', render: (text) => text || '-' },
    { title: 'IMEI', dataIndex: 'imei', key: 'imei', render: (text) => text || '-'},
    { title: i18n.t('device_type'), dataIndex: 'name', key: 'name' },
    { title: i18n.t('config'), dataIndex: 'configName', key: 'configName' },
    { title: i18n.t('config_sent_time'), dataIndex: 'sendAt', key: 'sendAt' },
    { title: i18n.t('create_time'), dataIndex: 'createAt', key: 'createAt' },
  ];

  const refresh = params => {
    props.onRefreshDeviceList(language, buildFilter(params));
  };

  return (
    <div>
      <SearchContainer>
        <Space direction="horizontal">
          <SelectItem
            dropdownMatchSelectWidth={300}
            style={{ marginTop: 0, minWidth: 200 }}
            onChanged={event => {
              setHadSend(event?.value);
            }}
            options={SEND_STATUS_OPTIONS}
            placeholder={i18n.t('config_send_status')}
            allowClear={true}
          />
          <SelectItem
            dropdownMatchSelectWidth={300}
            style={{ marginTop: 0, minWidth: 200 }}
            onChanged={event => {
              setPrivateConfigId(event?.value);
            }}
            options={props.configList}
            placeholder={i18n.t('select_config')}
            allowClear={true}
            value={privateConfigId}
          />
          <SelectItem
            dropdownMatchSelectWidth={300}
            style={{ marginTop: 0, minWidth: 200 }}
            onChanged={event => {
              setProductCode(event?.value);
            }}
            options={props.products}
            placeholder={i18n.t('select_device_type')}
          />
          <InputItem
            placeholder="MAC/SN"
            bordered={true}
            onChange={event => setMac(event.target?.value)}
            style={{ width: 200 }}
            showSearch={true}
          />
        </Space>
      </SearchContainer>
      <TableContainer>
        <Table
          rowKey={({ mac }) => mac}
          columns={tableColumns}
          rowSelection={rowSelection}
          dataSource={props.deviceList}
          size="small"
          loading={props.loading}
          pagination={false}
          scroll={{ y: document.body.clientHeight - 320 }}
          footer={() => props.deviceList.length > 0 && (
            <FooterContainer>
              <Space direction="horizontal" style={{ marginTop: 5, marginBottom: 5 }}>
                <Button
                  type="primary"
                  disabled={selectedMacs.length === 0}
                  size="middle"
                  onClick={() => {
                    setEditDeviceDialogVisible(true);
                  }}
                >
                  {i18n.t('change_config')}
                </Button>
                <Button
                  type="primary"
                  disabled={selectedMacs.length === 0}
                  size="middle"
                  onClick={() => {
                    deleteDevice(selectedMacs);
                  }}
                >
                  {i18n.t('menu_delete')}
                </Button>
                <label style={{ fontSize: 16 }}>
                  {i18n.t('table_footer').replace('@$', `${selectedMacs.length}`)}
                </label>
              </Space>
              <TablePagination
                total={props.total}
                current={props.currentPage}
                pageSize={props.pageSize}
                refresh={refresh}
              />
            </FooterContainer>
          )}
        />
      </TableContainer>

      {editDeviceDialogVisible && (
        <EditDevices
          visible={editDeviceDialogVisible}
          selectedRows={selectedMacs}
          onCancel={() => setEditDeviceDialogVisible(false)}
          onOk={({ code, configName }) => {
            setEditDeviceDialogVisible(false);
            if (code === 200) {
              setEditedConfigName(configName);
              setEditConfigTipVisible(true);
              clearSelected();
            } else {
              resultModal({
                title: i18n.t('edit_success_fail'),
                status: 'error',
              });
            }
          }}
        />
      )}
      <DeviceConfigTipModal
        visible={editConfigTipVisible}
        onOk={() => {
          setEditConfigTipVisible(false);
          setEditedConfigName('');
        }}
        configName={editedConfigName}
      />
    </div>
  );
}

const SearchContainer = styled.div`
  margin-bottom: 16px;
`;

const TableContainer = styled.div`
  min-height: 400px;
`;

const FooterContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const mapState2Props = state => {
  return {
    language: state.language.language,
    products: state.config.products,
    deviceList: state.device.deviceList,
    loading: state.device.tableLoading,
    total: state.device.total,
    currentPage: state.device.currentPage,
    pageSize: state.device.pageSize,
    configList: state.config.configList.map(({ id, name }) => ({
      label: name,
      value: id,
    })),
  };
};

const mapDispatch2Props = dispatch => ({
  onRefreshDeviceList: (language, filter) => dispatch(actions.onDeviceList(language, filter)),
  onRefreshConfigList: () => dispatch(actions.onRefreshConfigList()),
  onRefreshProducts: language => dispatch(actions.onRefreshProducts(language)),
});

export default connect(mapState2Props, mapDispatch2Props)(SentDevicesTab);
